import type { Work, WorkType } from "@/types/work";
import { works } from "@/mocks/works";

export const PAGE_SIZE = 9;

export function workTypeLabel(type: WorkType): string {
  return type === "tv" ? "电视剧" : "电影";
}

export function getWorkById(id: string | undefined): Work | undefined {
  if (!id) return undefined;
  return works.find((item) => item.id === id);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return String(views);
}

export interface FilterOptions {
  type: WorkType;
  genres: string[];
  regions: string[];
  years: number[];
}

export type SortKey = "hot" | "latest" | "rating";

function matchGenres(work: Work, genres: string[]): boolean {
  if (genres.length === 0) return true;
  return work.genres.some((g) => genres.includes(g));
}

export function filterWorks(options: FilterOptions): Work[] {
  return works.filter((work) => {
    if (work.type !== options.type) return false;
    if (!matchGenres(work, options.genres)) return false;
    if (options.regions.length > 0 && !options.regions.includes(work.region))
      return false;
    if (options.years.length > 0 && !options.years.includes(work.year))
      return false;
    return true;
  });
}

export function sortWorks(list: Work[], sort: SortKey): Work[] {
  const cloned = [...list];
  if (sort === "hot") {
    return cloned.sort((a, b) => b.views - a.views);
  }
  if (sort === "rating") {
    return cloned.sort((a, b) => b.rating - a.rating);
  }
  return cloned.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export function paginate<T>(list: T[], page: number, size: number = PAGE_SIZE): T[] {
  const start = (page - 1) * size;
  return list.slice(start, start + size);
}

export function countPages(total: number, size: number = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / size));
}

export function searchWorks(keyword: string, type?: WorkType | "all"): Work[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return works.filter((work) => {
    if (type && type !== "all" && work.type !== type) return false;
    const haystack = [
      work.title,
      work.director,
      work.region,
      ...work.genres,
      ...work.cast,
      work.synopsis,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelated(work: Work, limit = 4): Work[] {
  return works
    .filter((item) => item.id !== work.id)
    .map((item) => {
      let score = 0;
      score += item.genres.filter((g) => work.genres.includes(g)).length * 3;
      score += item.cast.filter((c) => work.cast.includes(c)).length * 4;
      if (item.region === work.region) score += 1;
      if (item.director === work.director) score += 2;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function getHotWorks(limit = 6): Work[] {
  return sortWorks(works, "hot").slice(0, limit);
}

export function getLatestWorks(limit = 8): Work[] {
  return sortWorks(works, "latest").slice(0, limit);
}

export interface NewsItem {
  date: string;
  title: string;
  summary: string;
}

export function getWorkNews(work: Work): NewsItem[] {
  const typeLabel = workTypeLabel(work.type);
  return [
    {
      date: work.updatedAt,
      title: `《${work.title}》${work.status}，${work.region}${typeLabel}资料更新`,
      summary: `本次更新补充了《${work.title}》的完整剧情简介、演职员表与${work.genres.join("、")}类型标签，方便影迷一站式查询。`,
    },
    {
      date: work.updatedAt,
      title: `《${work.title}》主创访谈：${work.director}谈创作幕后`,
      summary: `导演${work.director}分享了《${work.title}》在剧本打磨与选角上的考量，并谈到主演${work.cast[0]}在片场的一个即兴细节。`,
    },
    {
      date: work.updatedAt,
      title: `${work.cast[0]}新作品引关注，${typeLabel}爱好者期待值走高`,
      summary: `凭借在《${work.title}》中的表演，${work.cast[0]}近期讨论度持续上升，相关作品回顾专题同步上线。`,
    },
  ];
}